/**
 * Sidebar Component - Rail + Drawer Pattern (Hostinger Style)
 * Supports two modes: Float (overlay) and Expand (push content)
 */
import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInstance } from '../../contexts/InstanceContext';
import { getLogo, getBusinessName } from '../../lib/logos';
import { cn } from '../../lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FolderTree,
  Users,
  UserCog,
  Shield,
  Table2,
  ClipboardList,
  Wallet,
  CreditCard,
  BarChart3,
  FileText,
  PackageSearch,
  Utensils,
  Crown,
  Boxes,
  ArrowLeftRight,
  Settings,
  Building2,
  Tag,
  Ruler,
  Headphones,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  Briefcase,
  Printer,
  Calculator,
} from 'lucide-react';

// Context for sidebar state
const SidebarContext = createContext({
  isExpanded: false,
  isPinned: false,
  activeGroup: null,
});

export const useSidebar = () => useContext(SidebarContext);

// Icon map for dynamic rendering
const iconMap = {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FolderTree,
  Users,
  UserCog,
  Shield,
  Table2,
  ClipboardList,
  Wallet,
  CreditCard,
  BarChart3,
  FileText,
  PackageSearch,
  Utensils,
  Crown,
  Boxes,
  ArrowLeftRight,
  Settings,
  Building2,
  Tag,
  Ruler,
  Headphones,
  Briefcase,
  Printer,
  Calculator,
};

// Navigation structure
const navigationItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/',
    type: 'link',
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    icon: 'Package',
    type: 'group',
    children: [
      { label: 'Produtos', path: '/products', icon: 'Package' },
      { label: 'Serviços', path: '/services', icon: 'Briefcase' },
      { label: 'Categorias', path: '/categories', icon: 'FolderTree' },
      { label: 'Combos', path: '/combos', icon: 'Utensils' },
      { label: 'Clientes', path: '/customers', icon: 'Users' },
      { label: 'Fornecedores', path: '/suppliers', icon: 'Building2' },
      { label: 'Marcas', path: '/brands', icon: 'Tag' },
      { label: 'Unidades', path: '/units-of-measure', icon: 'Ruler' },
    ],
  },
  {
    id: 'operacoes',
    label: 'Operações',
    icon: 'ClipboardList',
    type: 'group',
    children: [
      { label: 'Mesas', path: '/tables', icon: 'Table2' },
      { label: 'Comandas', path: '/orders', icon: 'ClipboardList' },
      { label: 'Caixas', path: '/cashiers', icon: 'Wallet' },
    ],
  },
  {
    id: 'estoque',
    label: 'Estoque',
    icon: 'Boxes',
    type: 'group',
    children: [
      { label: 'Lotes', path: '/stock/batches', icon: 'Boxes' },
      { label: 'Movimentações', path: '/stock/movements', icon: 'ArrowLeftRight' },
      { label: 'Ordens de Compra', path: '/stock/purchase-orders', icon: 'ShoppingCart' },
    ],
  },
  {
    id: 'pricing',
    label: 'Precificação',
    icon: 'Calculator',
    path: '/pricing',
    type: 'link',
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    icon: 'BarChart3',
    type: 'group',
    children: [
      { label: 'Vendas', path: '/reports/sales', icon: 'BarChart3' },
      { label: 'Caixa', path: '/reports/cashier', icon: 'FileText' },
      { label: 'Estoque', path: '/reports/stock', icon: 'PackageSearch' },
    ],
  },
  {
    id: 'config',
    label: 'Config',
    icon: 'Settings',
    type: 'group',
    children: [
      { label: 'Usuários', path: '/users', icon: 'UserCog' },
      { label: 'Perfis', path: '/profiles', icon: 'Shield' },
      { label: 'Pagamentos', path: '/payment-methods', icon: 'CreditCard' },
      { label: 'Impressoras', path: '/settings/printers', icon: 'Printer' },
      { label: 'Personalização', path: '/settings/customization', icon: 'Tag' },
      { label: 'Sistema', path: '/settings', icon: 'Settings' },
    ],
  },
  {
    id: 'suporte',
    label: 'Suporte',
    icon: 'Headphones',
    path: '/tickets',
    type: 'link',
  },
];

// Sidebar Rail Item Component
function SidebarRailItem({ item, isActive, isGroupActive, onClick }) {
  const Icon = iconMap[item.icon] || Package;
  const active = item.type === 'link' ? isActive : isGroupActive;
  
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            data-testid={`rail-item-${item.id}`}
            className={cn(
              'w-16 h-16 flex flex-col items-center justify-center rounded-lg transition-all duration-200 cursor-pointer',
              active
                ? 'bg-primary/10 text-primary font-semibold shadow-sm ring-1 ring-primary/20'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Icon className="h-6 w-6" />
            <span className="text-[10px] font-medium mt-1.5 text-center leading-tight">
              {item.label}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Sidebar Drawer Component
function SidebarDrawer({ group, isOpen, isPinned, onTogglePin, onItemClick }) {
  const location = useLocation();
  
  if (!group || !isOpen) return null;
  
  return (
    <div 
      className={cn(
        'fixed left-[80px] top-14 h-[calc(100vh-3.5rem)] w-60 bg-card',
        'border-r border-border z-40',
        'animate-in slide-in-from-left duration-200',
        isPinned ? 'shadow-none' : 'shadow-xl'
      )}
      data-testid="sidebar-drawer"
    >
      {/* Drawer Header */}
      <div className="h-12 flex items-center justify-between px-3 border-b border-border bg-muted/30">
        <h2 className="font-semibold text-base">{group.label}</h2>
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isPinned ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "h-7 gap-1.5 text-xs",
                  isPinned && "bg-primary/10 text-primary hover:bg-primary/20"
                )}
                onClick={onTogglePin}
                data-testid="drawer-pin-btn"
              >
                {isPinned ? (
                  <>
                    <PanelLeftClose className="h-3.5 w-3.5" />
                    <span>Fixado</span>
                  </>
                ) : (
                  <>
                    <PanelLeft className="h-3.5 w-3.5" />
                    <span>Fixar</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isPinned ? 'Clique para flutuar o painel' : 'Clique para fixar o painel'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Drawer Content */}
      <ScrollArea className="h-[calc(100%-3rem)]">
        <nav className="p-2">
          {group.children?.map((child) => {
            const Icon = iconMap[child.icon] || Package;
            const isActive = location.pathname === child.path;
            
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={() => onItemClick()}
                data-testid={`drawer-item-${child.path.replace(/\//g, '-')}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm font-medium flex-1">{child.label}</span>
                <ChevronRight className="h-4 w-4 opacity-50" />
              </Link>
            );
          })}
        </nav>
      </ScrollArea>
    </div>
  );
}

// Main Sidebar Component
export default function Sidebar({ className, onExpandChange }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdminMaster, currentInstance } = useInstance();
  const logoSrc = getLogo(currentInstance, 'iconDark');
  const [activeGroup, setActiveGroup] = useState(null);
  const [isPinned, setIsPinned] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebar-pinned');
    return saved === 'true';
  });
  
  // Add Admin Master item if applicable
  const navItems = isAdminMaster 
    ? [...navigationItems, {
        id: 'admin',
        label: 'Admin',
        icon: 'Crown',
        type: 'group',
        children: [
          { label: 'Instâncias', path: '/admin/instances', icon: 'Crown' },
          { label: 'Tickets', path: '/admin/tickets', icon: 'Headphones' },
        ],
      }]
    : navigationItems;
  
  // Determine which group is active based on current path
  // Only for highlighting, not for opening drawer automatically
  useEffect(() => {
    // Don't auto-open drawer on page load or navigation
    // User must click to open the drawer
    if (!isPinned) {
      setActiveGroup(null);
    }
  }, [location.pathname, isPinned]);
  
  // Notify parent of expand state changes
  useEffect(() => {
    const isExpanded = !!activeGroup && isPinned;
    onExpandChange?.(isExpanded);
  }, [activeGroup, isPinned, onExpandChange]);
  
  // Save pin state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-pinned', isPinned.toString());
  }, [isPinned]);
  
  const handleRailItemClick = (item) => {
    if (item.type === 'link') {
      navigate(item.path);
      // Always close any open drawer when clicking a direct link
      setActiveGroup(null);
    } else if (item.type === 'group') {
      setActiveGroup(prev => prev === item.id ? null : item.id);
    }
  };
  
  const handleDrawerItemClick = () => {
    // Close drawer after click only if not pinned
    if (!isPinned) {
      // Keep open to show active state
    }
  };
  
  const handleTogglePin = () => {
    setIsPinned(prev => !prev);
  };
  
  const handleBackdropClick = () => {
    if (!isPinned) {
      setActiveGroup(null);
    }
  };
  
  const activeGroupData = navItems.find(item => item.id === activeGroup);
  const isDrawerOpen = !!activeGroup;
  
  return (
    <SidebarContext.Provider value={{ isExpanded: isDrawerOpen && isPinned, isPinned, activeGroup }}>
      {/* Sidebar Rail */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full w-[80px] bg-card border-r border-border z-50',
          'flex flex-col items-center py-4',
          className
        )}
        data-testid="sidebar-rail"
      >
        {/* Logo */}
        <Link 
          to="/" 
          className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center mb-6 shadow-sm"
          data-testid="sidebar-logo"
        >
          <img 
            src={logoSrc} 
            alt="SisPDV" 
            className="w-full h-full object-contain"
          />
        </Link>
        
        {/* Navigation Items */}
        <ScrollArea className="flex-1 w-full">
          <div className="flex flex-col items-center gap-1 px-2">
            {navItems.map((item) => {
              const isActive = item.type === 'link' && location.pathname === item.path;
              const isGroupActive = item.id === activeGroup;
              
              return (
                <SidebarRailItem
                  key={item.id}
                  item={item}
                  isActive={isActive}
                  isGroupActive={isGroupActive}
                  onClick={() => handleRailItemClick(item)}
                />
              );
            })}
          </div>
        </ScrollArea>
      </aside>
      
      {/* Sidebar Drawer */}
      <SidebarDrawer
        group={activeGroupData}
        isOpen={isDrawerOpen}
        isPinned={isPinned}
        onTogglePin={handleTogglePin}
        onItemClick={handleDrawerItemClick}
      />
      
      {/* Backdrop for closing drawer (only when not pinned) */}
      {isDrawerOpen && !isPinned && (
        <div
          className="fixed inset-0 z-30"
          onClick={handleBackdropClick}
          data-testid="sidebar-backdrop"
        />
      )}
    </SidebarContext.Provider>
  );
}
